/**
 * Helper: General
 * -----------------------------------------------------------------------------
 * General helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */

/**
 * Debounce functions.
 * - Calls a function when a user hasn't carried out an event for a specific
 *   amount of time.
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null;

  return function debounceFunc(...args) {
    const callNow = immediate && !timeout;

    function later() {
      timeout = null;
      if (!immediate) {
        callback.apply(this, args);
      }
    }

    window.clearTimeout(timeout);
    timeout = window.setTimeout(later, wait);

    if (callNow) {
      callback.apply(this, args);
    }
  };
}

/**
 * Handles pluralisation of strings based on Unicode plural rules.
 * - https://unicode-org.github.io/cldr-staging/charts/latest/supplemental/language_plural_rules.html
 * - Follows English plural rules.
 * - Zero + (One|Single) + (Two + Few) + (Other|Many|Plural).
 * @param {Object} strings - Strings object containing locale strings.
 * @param {Number} number - Number to use for plural rules.
 * @returns {String}
 */
export function handlePluralisation(strings, number) {
  if (strings.zero && number === 0) {
    return strings.zero;
  }

  const one = strings.one || strings.single;

  if (one && number === 1) {
    return one;
  }

  if (strings.two && number === 2) {
    return strings.two;
  }

  const many = strings.other || strings.many || strings.plural;

  if (strings.few) {
    if (many && strings.two && number === 3) {
      return strings.few;
    }

    if (!many && strings.two && number > 2) {
      return strings.few;
    }

    if (!many && number > 1) {
      return strings.few;
    }
  }

  return many;
}

/**
 * Test if a URL is a Shopify CDN URL.
 * - Returns true if URL contains:
 * -- cdn.shopify.com/
 * -- [custom-domain].[tld]/cdn/
 * -- [store].myshopify.com/cdn/
 * @param {String} url - URL to test.
 * @returns {Boolean}
 */
export function isCdnUrl(url) {
  const cdnUrls = [
    'cdn.shopify.com/',
    `${cnvs.store.shopDomain.replaceAll('/', '')}/cdn/`,
    `${cnvs.store.permanentDomain.replaceAll('/', '')}/cdn/`,
  ];

  return cdnUrls.some((cdnUrl) => url.includes(cdnUrl));
}

/**
 * Test if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @param {Object} [config] - Config options.
 * @param {HTMLElement} [config.container] - Container to check in.
 * @param {String} [config.direction] - Either `vertical` or `horizontal`.
 * @returns {Boolean} - True if element is at least partially in viewport.
 */
export function isVisible(element, { container, direction = 'vertical' } = {}) {
  let containerDimension = container
    ? container.offsetHeight
    : window.innerHeight;

  const elementRectangle = element.getBoundingClientRect();
  let elementStart = elementRectangle.top;
  let elementEnd = elementRectangle.bottom;

  if (direction === 'horizontal') {
    containerDimension = container ? container.offsetWidth : window.innerWidth;

    elementStart = elementRectangle.left;
    elementEnd = elementRectangle.right;
  }

  const startInView = elementStart >= 0 && elementStart < containerDimension;
  const endInView = elementEnd > 0 && elementEnd <= containerDimension;

  return startInView || endInView;
}

/**
 * Scrolls page smoothly to element.
 * @param {HTMLElement} element - Element to scroll to.
 * @param {Object} [config] - Additional data.
 * @param {Function} [config.callback] - Function run after completion.
 * @param {String} [config.headerState] - State of header when function is run.
 * @param {HTMLElement} [config.page] - Element to scroll.
 */
export function scrollToElement(
  element,
  { callback, headerState, page = window } = {},
) {
  let viewportFromPageTop = window.scrollY;

  if (page !== window) {
    viewportFromPageTop = 0;
  }

  /**
   * Get element scroll position.
   */
  const elementRectangle = element.getBoundingClientRect();
  const elementFromViewportTop = Math.floor(elementRectangle.top);
  let position = viewportFromPageTop + elementFromViewportTop;

  /**
   * Retrieve variables.
   */
  const headerHeight = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .replace('px', ''),
  );

  const fontSize = Number(
    getComputedStyle(document.documentElement).fontSize.replace('px', ''),
  );

  let spacingM = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--spacing-m')
      .replace('rem', ''),
  );

  spacingM *= fontSize;

  /**
   * Offset height of header and update position.
   * - Assumes that when header is collapsed it has a height of 0.
   * - If it doesn't then update each condition with a new variable.
   * - Don't use --header-visible-height as that isn't updated in time.
   */
  const threshold =
    headerState === 'collapsed' ? spacingM : headerHeight + spacingM;

  if (elementFromViewportTop === threshold) {
    return;
  }

  const dynamicHeaderHeight =
    elementFromViewportTop > threshold ? 0 : headerHeight;

  position -= dynamicHeaderHeight;
  position -= spacingM;

  /**
   * Disable smooth scroll if prefers reduced motion.
   */
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

  page.scroll({
    behavior,
    left: 0,
    top: position,
  });

  if (typeof callback === 'function') {
    callback();
  }
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Object} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - Values to grow or shrink each side of the
 * root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {Function}
 */
export function setIntersectionObserver({
  callback,
  element,
  rootMargin = '0px',
  threshold = 0.5,
}) {
  const options = {
    root: null,
    rootMargin,
    threshold,
  };

  const observer = new IntersectionObserver(callback, options);
  observer.observe(element);
}

/**
 * Throttle functions.
 * - Calls a function at intervals of a specified amount of time while the user
 *   is still carrying out the event.
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null;
  let initialCall = true;

  return function throttleFunc(...args) {
    const callNow = immediate && initialCall;

    function next() {
      callback.apply(this, args);
      timeout = null;
    }

    if (callNow) {
      initialCall = false;
      next();
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait);
    }
  };
}

/**
 * Remove square brackets from a string.
 * @param {String} string - String to remove brackets from.
 * @returns {String} - Formatted string.
 */
export function removeBrackets(string) {
  return string.replace(/\[.*?\]/g, '').trim();
}

/**
 * Compute whether date is within start and end time.
 * - If value is 'false'/false then use fallback value, this adds support for no
 *   start/end dates.
 * - Uses YYYY-MM-DDTHH:MM as date time format.
 * - All times are compared in UTC to avoid timezone issues.
 * - Uses server time for current time to prevent client clock manipulation.
 * @param {Boolean|String} startTime - Start date time format.
 * @param {Boolean|String} endTime - End date time format.
 * @param {Number} [serverTime] - Server timestamp in seconds.
 * @returns {Boolean}
 */
export function dateTime(startTime, endTime, serverTime) {
  // Helper function to ensure date string has timezone info
  const ensureUTC = (dateString) => {
    if (!dateString || dateString === 'false') return dateString;
    // Check if the string already has timezone info (Z, +, or -)
    if (/[Z+-]\d*:?\d*$/.test(dateString)) {
      return dateString;
    }

    // If no timezone info and we have server time, treat as server local time
    if (serverTime) {
      // Get server timezone offset from the server timestamp
      const serverDate = new Date(serverTime * 1000);
      const serverTimezoneOffset = serverDate.getTimezoneOffset();

      // Convert offset to hours and minutes for timezone string
      const offsetHours = Math.floor(Math.abs(serverTimezoneOffset) / 60);
      const offsetMinutes = Math.abs(serverTimezoneOffset) % 60;
      const offsetSign = serverTimezoneOffset <= 0 ? '+' : '-';
      const offsetString = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

      return `${dateString}${offsetString}`;
    }

    // Fallback: If no server time provided, assume UTC
    return `${dateString}Z`;
  };

  // Convert start time to UTC
  const startDateTime =
    startTime === 'false' || !startTime
      ? new Date('2000-01-01T12:00Z')
      : new Date(ensureUTC(startTime));

  // Convert end time to UTC
  const endDateTime =
    endTime === 'false' || !endTime
      ? new Date('2100-01-01T12:00Z')
      : new Date(ensureUTC(endTime));

  // Use server time if provided, otherwise fallback to client time
  const currentDateTime = serverTime ? new Date(serverTime * 1000) : new Date();

  // Convert current time to UTC for comparison
  const currentUTCDateTime = new Date(
    Date.UTC(
      currentDateTime.getUTCFullYear(),
      currentDateTime.getUTCMonth(),
      currentDateTime.getUTCDate(),
      currentDateTime.getUTCHours(),
      currentDateTime.getUTCMinutes(),
      currentDateTime.getUTCSeconds(),
    ),
  );

  /**
   * Check if the parsed dates are valid before using them.
   */
  if (
    isNaN(startDateTime.getTime()) ||
    isNaN(endDateTime.getTime()) ||
    isNaN(currentUTCDateTime.getTime())
  ) {
    console.error('Invalid date encountered:', {
      startTime,
      endTime,
      serverTime,
      processedStartTime: ensureUTC(startTime),
      processedEndTime: ensureUTC(endTime),
      startDateTime: isNaN(startDateTime.getTime())
        ? 'INVALID'
        : startDateTime.toString(),
      endDateTime: isNaN(endDateTime.getTime())
        ? 'INVALID'
        : endDateTime.toString(),
      currentUTCDateTime: isNaN(currentUTCDateTime.getTime())
        ? 'INVALID'
        : currentUTCDateTime.toString(),
    });
    return false;
  }

  /**
   * Check if the current date is within the specified range.
   */
  return (
    currentUTCDateTime >= startDateTime && currentUTCDateTime <= endDateTime
  );
}

/**
 * Wait for a window variable to be defined before proceeding with callback.
 * @example waitFor('nosto', callback)
 * @param {String} variable - Named window variable to wait for.
 * @param {Function} callback -Function to execute when interval is cleared.
 */
export function waitFor(variable, callback) {
  const timeout = 10000;
  const loop = 500;
  const start = performance.now();

  const interval = setInterval(() => {
    if (!window[variable]) {
      const difference = performance.now() - start;

      if (difference > timeout) {
        window.console.error(`Error: ${variable} not loaded.`);
        clearInterval(interval);
      }

      return;
    }

    clearInterval(interval);
    callback();
  }, loop);
}

/**
 * Enters fullscreen.
 */
export function enterFullscreen(video) {
  if (video.requestFullscreen) {
    video.requestFullscreen();
  } else if (video.mozRequestFullScreen) {
    video.mozRequestFullScreen();
  } else if (video.webkitRequestFullscreen) {
    video.webkitRequestFullscreen();
  } else if (video.webkitRequestFullScreen) {
    video.webkitRequestFullScreen();
  } else if (video.msRequestFullscreen) {
    video.msRequestFullscreen();
  } else if (video.webkitSupportsFullscreen) {
    video.webkitEnterFullscreen();
  }
}

/**
 * @param {string} markets - String containing Markets, f.e. [DE, UK].
 * @param {string | undefined} keyword
 */
export function isAccessibleOnMarket(markets, keyword = null) {
  const hasBrackets = markets.includes('[') && markets.includes(']');
  const correctMarkets = markets.toUpperCase().replace('UK', 'GB');
  const splitMarkets = correctMarkets.split('[').pop().split(']')[0].split(',');

  if (!correctMarkets || correctMarkets === keyword || !hasBrackets) {
    return true;
  }

  const countryCode = cnvs.store.country.toUpperCase().trim();
  const marketCode = cnvs.store.marketCode.toUpperCase().trim();

  for (const market of splitMarkets) {
    const trimmedCode = market.trim();
    if (trimmedCode === countryCode || trimmedCode === marketCode) {
      return true;
    }
  }

  return false;
}

/**
 * Server time management utilities
 * -----------------------------------------------------------------------------
 * Functions to fetch and manage server time from Shopify to prevent client clock manipulation.
 */

let serverTimeCache = null;
let serverTimeInterval = null;
let initialPageLoadTime = null;

/**
 * Fetch current server time from Shopify server by pinging cart endpoint.
 * @returns {Promise<number>} Server timestamp in seconds
 */
export async function fetchServerTime() {
  try {
    // Make a simple request to any Shopify endpoint to get server time from headers
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get server time from response headers (Date header contains server time)
    const dateHeader = response.headers.get('date');
    if (dateHeader) {
      const serverTime = Math.floor(new Date(dateHeader).getTime() / 1000);
      serverTimeCache = serverTime;
      return serverTime;
    }
    // Fallback to client time if no date header
    const fallbackTime = Math.floor(Date.now() / 1000);
    serverTimeCache = fallbackTime;
    return fallbackTime;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching server time:', error);
    // Fallback to client time
    const fallbackTime = Math.floor(Date.now() / 1000);
    serverTimeCache = fallbackTime;
    return fallbackTime;
  }
}

/**
 * Get current server time (cached or calculated from initial fetch).
 * @returns {number} Current server timestamp in seconds
 */
export function getCurrentServerTime() {
  if (serverTimeCache === null) {
    // If no server time has been fetched, return client time
    return Math.floor(Date.now() / 1000);
  }

  if (initialPageLoadTime === null) {
    // If initial page load time hasn't been set, set it now
    initialPageLoadTime = performance.now();
    return serverTimeCache;
  }

  // Calculate elapsed time since page load in seconds
  const elapsedSeconds = Math.floor(
    (performance.now() - initialPageLoadTime) / 1000,
  );

  return serverTimeCache + elapsedSeconds;
}

/**
 * Start server time tracking with automatic updates.
 * @param {Function} [callback] - Optional callback to execute on each time update
 * @param {number} [interval=1000] - Update interval in milliseconds
 * @returns {Function} Function to stop the tracking
 */
export function startServerTimeTracking(callback = null, interval = 1000) {
  // Clear any existing interval
  if (serverTimeInterval) {
    clearInterval(serverTimeInterval);
  }

  // Set initial page load time if not already set
  if (initialPageLoadTime === null) {
    initialPageLoadTime = performance.now();
  }

  // Start interval to update server time
  serverTimeInterval = setInterval(() => {
    const currentTime = getCurrentServerTime();

    // Execute callback if provided
    if (callback && typeof callback === 'function') {
      callback(currentTime);
    }
  }, interval);

  // Return function to stop tracking
  return () => {
    if (serverTimeInterval) {
      clearInterval(serverTimeInterval);
      serverTimeInterval = null;
    }
  };
}

/**
 * Stop server time tracking.
 */
export function stopServerTimeTracking() {
  if (serverTimeInterval) {
    clearInterval(serverTimeInterval);
    serverTimeInterval = null;
  }
}

/**
 * Initialize server time (fetch initial time and optionally start tracking).
 * @param {boolean} [startTracking=false] - Whether to start automatic tracking
 * @param {Function} [callback] - Optional callback for tracking updates
 * @returns {Promise<number>} Initial server timestamp
 */
export async function initializeServerTime(
  startTracking = false,
  callback = null,
) {
  const initialTime = await fetchServerTime();

  if (startTracking) {
    startServerTimeTracking(callback);
  }

  return initialTime;
}

/**
 * Reset server time cache and tracking.
 */
export function resetServerTime() {
  serverTimeCache = null;
  initialPageLoadTime = null;
  stopServerTimeTracking();
}
